import { useModals } from 'almanac-ui/Modal'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { BrandKit, SelectedImage } from 'types'
import { useUploadMedia } from './useUploadMedia'
import { ProcessingToastMessage } from './ProcessingToastMessage'
import { ImageMenuProps } from '../Types'
import { CreateMediaFileParams } from 'api/clients/MediaFileClient'

export type Provider =
  | 'uploads'
  | 'device'
  | 'ai'
  | 'hardcodedAi'
  | 'gifs'
  | 'stockPhoto'
  | 'stockVideo'
  | 'link'
  | 'brandKits'
  | 'blazeRecommendations'

export const PROVIDERS_REQUIRING_DOWNLOAD: Provider[] = [
  'stockPhoto',
  'stockVideo',
  'ai',
  'gifs',
  'link',
  'hardcodedAi',
  'blazeRecommendations',
] as const

export type ImageMenuSelectedImage = SelectedImage & { provider: Provider }

interface ImageMenuContextValue {
  selectedImages: ImageMenuSelectedImage[]
  setSelectedImages: (images: ImageMenuSelectedImage[]) => void
  toggleSelectedImage: (image: ImageMenuSelectedImage) => void
  searchTerm: string
  setSearchTerm: (term: string) => void
  onUse: (images: ImageMenuSelectedImage[]) => void
  provider: string
  setProvider: (provider: string) => void
  maxCount: number
  hiddenProviders: Provider[]
  onUploadProgress?: (progress: number) => void
  onUploadStart?: (files: File[]) => Promise<void>
  onUploadEnd?: () => void
  brandKitId?: number
  setBrandKitId: (id?: number) => void
  brandKits?: BrandKit[]
  accept?: string
  initialQuery?: string
  initialBrandKitId?: number
  isConfirming: boolean
  setIsConfirming: (isConfirming: boolean) => void
  showProcessingToast?: boolean
  createParams?: Partial<CreateMediaFileParams>
  action?: 'Insert' | 'Add' | 'Change'
}

export const ImageMenuContext = createContext<ImageMenuContextValue>({
  selectedImages: [],
  setSelectedImages: () => {},
  toggleSelectedImage: () => {},
  searchTerm: '',
  setSearchTerm: () => {},
  onUse: () => {},
  provider: '',
  setProvider: () => {},
  maxCount: Infinity,
  hiddenProviders: [],
  brandKitId: undefined,
  setBrandKitId: () => {},
  brandKits: [],
  accept: '',
  initialQuery: '',
  initialBrandKitId: undefined,
  isConfirming: false,
  setIsConfirming: () => {},
  showProcessingToast: true,
  action: 'Insert',
})

const ImageMenuContextProvider = ImageMenuContext.Provider

type LoadedPercentagesMap = { [key: string]: number }

/**
 * ImageMenuProvider Component API Specification
 *
 * A context provider component that manages the state and functionality for a media selection menu.
 * This component handles media selection and uploading (optionally).
 *
 * @component
 *
 * Providers of media that can be uploaded:
 * - 'device': Uploads media from the device
 * - 'link': Uploads media from a link
 * - 'ai': Uploads media from AI
 * - 'gifs': Uploads media from gifs
 * - 'stockPhoto': Stock photos
 * - 'stockVideo': Stock videos
 *
 * Providers of media that are already in the server:
 * - 'uploads': Media that is in the user's uploads folder
 * - 'brandKits': Media that is in one of the user's brand kits
 * - 'hardcodedAi': Existing media that is used as hardcoded AI examples
 *
 * Props:
 * @param {boolean} [uploadMedia] - Whether to upload the selected media from the providers that it can upload before
 *                                  calling onUse, defaults to false
 * @param {Function} onUse - Callback function that will be called with the selected media.
 *                           - If uploadMedia is true:
 *                               it will upload the media from the providers that it can upload and call
 *                               this callback with the uploaded media and the selected media from the providers
 *                               of media that are already in the server.
 *                           - If uploadMedia is false:
 *                               it will be called with the selected media.
 * @param {number} [maxCount] - Maximum number of media that can be selected, defaults to Infinity
 * @param {Provider[]} [hiddenProviders=[]] - List of providers to hide from the menu
 * @param {Function} [onUploadProgress] - Callback for upload progress updates, if uploadMedia is true
 * @param {Function} [onUploadStart] - Callback called when upload starts, if uploadMedia is true
 * @param {Function} [onUploadEnd] - Callback called when upload completes, if uploadMedia is true
 * @param {string} [accept] - Accepted MIME types for file selection, defaults to ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES
 * @param {string} [initialQuery] - Initial search term for the stock photo/video providers
 * @param {number} [initialBrandKitId] - Initial brand kit ID to select
 * @param {Partial<CreateMediaFileParams>} [createParams] - The params to use when creating media
 * @param {boolean} [showProcessingToast] - Whether to show upload progress toast, defaults to true
 *
 * Context Value:
 * The provider exposes the following values through context:
 * - selectedImages: ImageMenuSelectedImage[] - Currently selected media
 * - setSelectedImages: Function to update selected media
 * - toggleSelectedImage: Function to toggle media selection
 * - searchTerm: string - Current search term
 * - setSearchTerm: Function to update search term
 * - provider: string - Current selected provider
 * - setProvider: Function to update provider
 * - brandKitId: number | undefined - Current selected brand kit id
 * - setBrandKitId: Function to update the selected brand kit id
 * - brandKits: BrandKit[] - Available brand kits
 * - isConfirming: boolean - Whether an upload is in progress
 *
 * Usage:
 * ```tsx
 * <ImageMenuProvider
 *   onUse={(images) => handleImages(images)}
 *   uploadMedia={true}
 *   maxCount={5}
 *   hiddenProviders={['ai']}
 *   onUploadProgress={(progress) => console.log(progress)}
 *   onUploadStart={(files) => console.log(files)}
 *   onUploadEnd={() => console.log('upload completed')}
 *   accept={ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES}
 *   initialQuery="nature"
 *   initialBrandKitId={1}
 * >
 *   <YourImageMenuComponent />
 * </ImageMenuProvider>
 * ```
 */
export const ImageMenuProvider = ({
  onUse,
  uploadMedia = false,
  maxCount = Infinity,
  hiddenProviders = [],
  onUploadProgress,
  onUploadStart,
  onUploadEnd,
  children,
  accept = ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES,
  initialQuery,
  initialBrandKitId,
  showProcessingToast = true,
  defaultProvider,
  createParams,
  action = 'Insert',
  closeOnUse = true,
}: PropsWithChildren<ImageMenuProps>) => {
  const { closeModal } = useModals()
  const [isConfirming, setIsConfirming] = useState(false)
  const [selectedImages, setSelectedImages] = useState<ImageMenuSelectedImage[]>([])
  const [searchTerm, setSearchTerm] = useState(initialQuery || '')
  const [provider, setProvider] = useState(defaultProvider || '')
  const [loadedPercentages, setLoadedPercentages] = useState<LoadedPercentagesMap>({})

  const handlePercentages = (percentage: number, index?: number) => {
    if (index !== undefined) setLoadedPercentages((prev) => ({ ...prev, [index]: percentage }))
  }
  const totalPercentage =
    Object.values(loadedPercentages).reduce((acc, curr) => acc + curr, 0) / Object.values(loadedPercentages).length

  const progressHandler = onUploadProgress || handlePercentages

  const { data: brandKits } = useBrandKits({
    select: (data) => data.filter((brandKit) => brandKit.media_files.length > 0),
    cacheTime: 1000 * 60 * 5,
  })

  const [brandKitId, setBrandKitId] = useState<number | undefined>(initialBrandKitId || brandKits?.[0]?.id)

  useEffect(() => {
    if (brandKits && brandKits.length > 0 && !brandKitId) setBrandKitId(brandKits?.[0]?.id)
  }, [brandKitId, brandKits])

  const toggleSelectedImage = (image: ImageMenuSelectedImage) => {
    setSelectedImages((images: ImageMenuSelectedImage[]) => {
      if (images.some(({ url }) => url === image.url)) {
        return images.filter(({ url }) => url !== image.url)
      }

      if (selectedImages.length >= maxCount) {
        return [...images.slice(1), image]
      } else {
        return [...images, image]
      }
    })
  }

  const { uploadImages } = useUploadMedia({
    onUploadProgress: progressHandler,
    setIsConfirming,
    onUploadStart,
    onUploadEnd,
    createParams,
  })

  return (
    <ImageMenuContextProvider
      value={{
        selectedImages,
        setSelectedImages,
        toggleSelectedImage,
        searchTerm,
        setSearchTerm,
        onUse: async (images: ImageMenuSelectedImage[]) => {
          // if uploadMedia is true, we upload the images
          // otherwise we use the onUse callback
          if (uploadMedia) {
            const uploadedImages = await uploadImages(images)
            onUse(uploadedImages as ImageMenuSelectedImage[])
          } else {
            onUse(images)
          }
          if (closeOnUse) {
            closeModal()
          }
        },
        provider,
        setProvider,
        maxCount,
        hiddenProviders,
        onUploadProgress: progressHandler,
        onUploadStart,
        onUploadEnd,
        brandKitId,
        setBrandKitId,
        brandKits,
        accept,
        initialQuery,
        initialBrandKitId,
        isConfirming,
        setIsConfirming,
        showProcessingToast,
        createParams,
        action,
      }}
    >
      {children}
      {showProcessingToast && isConfirming && !onUploadProgress && (
        <ProcessingToastMessage percentage={totalPercentage} />
      )}
    </ImageMenuContextProvider>
  )
}

export const useImageMenuContext = () => {
  return useContext(ImageMenuContext)
}
